import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import { ModalLarge, IModalChildProps } from '../../../common/modal-large';
import { IOrderDetails, IOrderDropShip } from '../../../../models/order';
import './modal-styles.css';
import { RootState } from '../../../../store';
import { connect } from 'react-redux';
import { FieldValidation, validate } from '../../../../validation/validation';
import * as Validators from '../../../../validation/validators';
import TextInputLabelOptional from '../../../inputs/text-input-label-optional';
import { IConstants } from '../../../../models/pro-sites';
import { loadConstants } from '../../../../store/externals/externals-actions';
import SelectInputModal from '../../../inputs/select-input-modal';
import {
    updateDeliverTo,
    loadOrder,
    clearDeliverTo,
    updateSelect,
} from '../../../../store/order/order-actions';
import { setIsBusy } from '../../../../store/api/api-status-action';
import { IITAPISResult } from '../../../../models/result-models';
import Modal from '../../../common/modal';
import { ISession } from '../../../../models/pro-sites';
import * as CON from '../../../common/constants';
import { gaEvent } from '../../../../hooks/ga-hook';
interface IDropShipModal extends IModalChildProps {
    order: IOrderDetails | null;
    constants: IConstants | null;
    loadConstants: () => Promise<void>;
    updateDeliverTo: (
        address: IOrderDropShip,
        orderNumber: string
    ) => Promise<void>;
    loadOrder: (orderNumber: string) => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
    updateDeliverToResponse: IITAPISResult | null;
    clearDeliverTo: () => { type: string };
    updateSelect: (select: number) => { type: string };
    session: ISession | null;
}

const DropShipModal = ({
    onAccept,
    onClose,
    order,
    constants,
    loadConstants,
    updateDeliverTo,
    loadOrder,
    setIsBusy,
    clearDeliverTo,
    updateSelect,
    session,
    updateDeliverToResponse,
}: IDropShipModal) => {
    type errors = {
        phone?: '';
        city?: '';
        country?: '';
        firstName?: '';
        lastName?: '';
        postalCode?: '';
        state?: '';
        street1?: '';
    };
    const intl = useIntl();
    const [error, setError] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorsState, setErrorsState] = useState<errors>({});
    const [address, setAddress] = useState<IOrderDropShip>({
        firstName: '',
        lastName: '',
        phone: '',
        street1: '',
        street2: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
    });
    useEffect(() => {
        if (!updateDeliverToResponse) return;

        if (updateDeliverToResponse!.returnCodeType === 'Error') {
            setError(true);
        } else {
            setSuccess(true);
        }
    }, [updateDeliverToResponse]);
    useEffect(() => {
        if (!constants) {
            loadConstants();
        }
    }, [constants, loadConstants]);

    useEffect(() => {
        return () => {
            clearDeliverTo();
            setConfirmed(false);
            setError(false);
            setSuccess(false);
        };
    }, []);

    const fieldValidations = [
        new FieldValidation('firstName', 'First Name', [
            Validators.required(
                intl.formatMessage(
                    {
                        id: 'Required_Field_Message',
                        defaultMessage: '{0} is required',
                    },
                    {
                        0: intl.formatMessage({ id: 'First_Name' }),
                    }
                )
            ),
        ]),
        new FieldValidation('lastName', 'Last Name', [
            Validators.required(
                intl.formatMessage(
                    {
                        id: 'Required_Field_Message',
                        defaultMessage: '{0} is required',
                    },
                    {
                        0: intl.formatMessage({ id: 'Last_Name' }),
                    }
                )
            ),
        ]),
        new FieldValidation('street1', 'Address Line 1', [
            Validators.required(),
        ]),
        new FieldValidation('city', 'City', [Validators.required()]),
        new FieldValidation('state', 'State', [Validators.required()]),
        new FieldValidation('country', 'Country', [Validators.required()]),
        new FieldValidation('phone', 'Contact Phone', [
            Validators.isPhone(address.phone),
        ]),
        new FieldValidation('postalCode', 'Postal Code', [
            Validators.minLength(5),
        ]),
    ];

    const formIsValid = (): boolean => {
        if (session!.country !== CON.US) {
            fieldValidations.splice(4, 1);
        }
        const errors: errors = validate(address, fieldValidations);

        setErrorsState(errors);

        return Object.keys(errors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const name = e.target.name;
        setAddress({
            ...address,
            [name]: value,
        });
    };
    const handleStateSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setAddress({
            ...address,
            state: event.target.value,
        });
    };
    const handleCountrySelect = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setAddress({
            ...address,
            country: event.target.value,
        });
    };
    const onSave = () => {
        if (!formIsValid()) {
            return;
        }

        setConfirmed(true);
    };
    const onConfirm = () => {
        gaEvent(
            'Estore_Order_Updates',
            'Dropship_Modal_Update',
            order!.billTo.accountNumber
        );

        setIsBusy(true);
        updateDeliverTo(address, order!.orderNumber).then(() => {
            setIsBusy(false);
        });
    };

    const onSubmit = () => {
        setConfirmed(false);
        setSuccess(false);
        setError(false);
        updateSelect(0);
        onClose();
        loadOrder(order!.orderNumber);
    };
    return (
        <>
            {confirmed ? (
                <Modal>
                    <>
                        <div className="modal-header">
                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label={intl.formatMessage({ id: 'Close' })}
                                onClick={onClose}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                            <h2 className="modal-title">
                                <FormattedMessage
                                    id="Change_Shipping_Address"
                                    defaultMessage="Change Shipping Address"
                                />
                                *
                            </h2>
                        </div>
                        {error && (
                            <p className="text-center modal-spacing">
                                <strong>
                                    <FormattedMessage
                                        id="Something_went_wrong_address"
                                        defaultMessage="Something went wrong, please verify your
                                    address is correct and retry."
                                    />
                                </strong>
                            </p>
                        )}
                        {success && !error && (
                            <p className="text-center modal-spacing">
                                <span className="fa fa-check icon-green"></span>
                                <strong>
                                    <FormattedMessage
                                        id="Shipping_address_successfully_changed"
                                        defaultMessage="Shipping address successfully changed"
                                    />
                                </strong>
                            </p>
                        )}
                        {!error && !success && (
                            <p className="text-center modal-spacing">
                                <strong>
                                    <FormattedMessage
                                        id="Are_you_sure_address"
                                        defaultMessage="Are you sure you want to change your
                                    shipping address?"
                                    />
                                </strong>
                                <br />
                                <FormattedMessage
                                    id="Changing_shipping_impact"
                                    defaultMessage="Changing your shipping address may impact your
                                expected delivery time and may include
                                additional charges."
                                />
                            </p>
                        )}

                        <div className="modal-footer">
                            {error && confirmed && !success && (
                                <>
                                    <button
                                        className="btn btn-info"
                                        onClick={() => {
                                            setConfirmed(false);
                                            setError(false);
                                            onClose();
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Close"
                                            defaultMessage="Close"
                                        />
                                    </button>
                                    <button
                                        className="btn btn-info"
                                        onClick={() => {
                                            setConfirmed(false);
                                            setError(false);
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Retry"
                                            defaultMessage="Retry"
                                        />
                                    </button>
                                </>
                            )}

                            {confirmed && !error && !success && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={() => {
                                            setConfirmed(false);
                                            onClose();
                                        }}
                                    >
                                        <FormattedMessage
                                            id="Cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </button>
                                    <button
                                        className="btn btn-info"
                                        onClick={onConfirm}
                                    >
                                        <FormattedMessage
                                            id="Continue"
                                            defaultMessage="Continue"
                                        />
                                    </button>
                                </>
                            )}

                            {success && confirmed && !error && (
                                <button
                                    className="btn btn-secondary"
                                    onClick={onSubmit}
                                >
                                    <FormattedMessage
                                        id="Close"
                                        defaultMessage="Close"
                                    />
                                </button>
                            )}
                        </div>
                    </>
                </Modal>
            ) : (
                <ModalLarge>
                    <>
                        <div className="modal-header">
                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label={intl.formatMessage({ id: 'Close' })}
                                onClick={onClose}
                                id="closeModalDropShip"
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                            <h2 className="modal-title">
                                <FormattedMessage
                                    id="Change_Shipping_Address"
                                    defaultMessage="Change Shipping Address"
                                />
                                *
                            </h2>
                        </div>
                        {!confirmed && !success && !error && (
                            <div className="modal-body clearfix">
                                <div className="col-md-6">
                                    <div
                                        className="form-horizontal"
                                        role="form"
                                    >
                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="First_Name"
                                                    defaultMessage="First Name"
                                                />
                                            </label>
                                            <div className="col-md-8">
                                                <TextInputLabelOptional
                                                    name="firstName"
                                                    className="form-control"
                                                    value={address.firstName}
                                                    maxLength={30}
                                                    onChange={handleChange}
                                                    error={
                                                        errorsState.firstName
                                                    }
                                                />
                                                <span className="required"></span>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="Last_Name"
                                                    defaultMessage="Last Name"
                                                />
                                            </label>
                                            <div className="col-md-8">
                                                <TextInputLabelOptional
                                                    className="form-control"
                                                    name="lastName"
                                                    value={address.lastName}
                                                    maxLength={30}
                                                    onChange={handleChange}
                                                    error={errorsState.lastName}
                                                />
                                                <span className="required"></span>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="Contact_Phone"
                                                    defaultMessage="Contact Phone"
                                                />
                                            </label>
                                            <div className="col-md-8">
                                                <TextInputLabelOptional
                                                    className="form-control"
                                                    name="phone"
                                                    value={address.phone}
                                                    maxLength={30}
                                                    onChange={handleChange}
                                                    error={errorsState.phone}
                                                />
                                                <span className="required"></span>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="Address_Line_1"
                                                    defaultMessage="Address Line 1"
                                                />
                                            </label>
                                            <div className="col-md-8">
                                                <TextInputLabelOptional
                                                    className="form-control"
                                                    name="street1"
                                                    value={address.street1}
                                                    maxLength={30}
                                                    onChange={handleChange}
                                                    error={errorsState.street1}
                                                />
                                                <span className="required"></span>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="Address_Line_2"
                                                    defaultMessage="Address Line 2"
                                                />
                                            </label>
                                            <div className="col-md-8">
                                                <TextInputLabelOptional
                                                    className="form-control"
                                                    name="street2"
                                                    value={address.street2}
                                                    maxLength={30}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div
                                        className="form-horizontal"
                                        role="form"
                                    >
                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="City"
                                                    defaultMessage="City"
                                                />
                                            </label>
                                            <div className="col-md-8">
                                                <TextInputLabelOptional
                                                    className="form-control"
                                                    name="city"
                                                    value={address.city}
                                                    maxLength={30}
                                                    onChange={handleChange}
                                                    error={errorsState.city}
                                                />
                                                <span className="required"></span>
                                            </div>
                                        </div>

                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="State_Province"
                                                    defaultMessage="State/Province"
                                                />
                                            </label>
                                            {session!.country !== CON.US ? (
                                                <div className="col-md-8">
                                                    <TextInputLabelOptional
                                                        className="form-control"
                                                        name="state"
                                                        value={address.state}
                                                        maxLength={30}
                                                        onChange={handleChange}
                                                        error={
                                                            errorsState.state
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <div className="col-md-8">
                                                    {constants && (
                                                        <SelectInputModal
                                                            name="state"
                                                            className="form-control"
                                                            value={
                                                                address.state
                                                            }
                                                            options={constants.states.map(
                                                                (st) => ({
                                                                    value: st.code,
                                                                    text: st.name,
                                                                })
                                                            )}
                                                            onChange={
                                                                handleStateSelect
                                                            }
                                                            error={
                                                                errorsState.state
                                                            }
                                                            hasDefaultOption={
                                                                true
                                                            }
                                                            defaultOption={
                                                                'Select'
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="Postal_Code"
                                                    defaultMessage="Postal Code"
                                                />
                                            </label>

                                            <div className="col-md-8">
                                                <TextInputLabelOptional
                                                    className="form-control"
                                                    name="postalCode"
                                                    value={address.postalCode}
                                                    maxLength={30}
                                                    onChange={handleChange}
                                                    error={
                                                        errorsState.postalCode
                                                    }
                                                />
                                                <span className="required"></span>
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="control-label col-md-4">
                                                <FormattedMessage
                                                    id="Country"
                                                    defaultMessage="Country"
                                                />
                                            </label>
                                            <div className="col-md-8">
                                                {constants && (
                                                    <SelectInputModal
                                                        name="country"
                                                        className="form-control"
                                                        onChange={
                                                            handleCountrySelect
                                                        }
                                                        value={address.country}
                                                        options={constants.countries.map(
                                                            (c) => ({
                                                                value: c.code,
                                                                text: c.name,
                                                            })
                                                        )}
                                                        hasDefaultOption={true}
                                                        defaultOption={'Select'}
                                                        error={
                                                            errorsState.country
                                                        }
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p>
                                    *
                                    <FormattedMessage
                                        id="Additional_Charges_May_Apply"
                                        defaultMessage="Additional Charges May Apply"
                                    />
                                </p>
                            </div>
                        )}
                        <div className="modal-footer">
                            {!success && !error && (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        id="cancelButtonDropShip"
                                        onClick={onClose}
                                    >
                                        <FormattedMessage
                                            id="Cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </button>
                                    <button
                                        className="btn btn-info"
                                        id="saveButtonDropShip"
                                        onClick={onSave}
                                    >
                                        <FormattedMessage
                                            id="Save"
                                            defaultMessage="Save"
                                        />
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                </ModalLarge>
            )}
        </>
    );
};
const mapStateToProps = (state: RootState) => {
    return {
        constants: state.constants,
        updateDeliverToResponse: state.updateDeliverToResponse
            ? state.updateDeliverToResponse.data!
            : null,
        order: state.order ? state.order.data! : null,
        session: state.session ? state.session.data! : null,
        selected: state.select,
    };
};

const mapDispatchToProps = {
    loadConstants,
    updateDeliverTo,
    loadOrder,
    setIsBusy,
    clearDeliverTo,
    updateSelect,
};

const DropShipModalContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(DropShipModal);

export default DropShipModalContainer;
